import React, { useState, useEffect, useRef } from 'react';
import { orderAPI, settingsAPI } from '../utils/api';
import { 
  Wine, 
  Volume2, 
  VolumeX,
  CheckCircle,
  PlayCircle,
  Clock,
  Receipt,
  ChefHat,
  Home,
  RefreshCw,
  Coffee
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Beverage categories
const BEVERAGE_CATEGORIES = ["Smoothies", "Juice", "Cocktails", "Long Drinks", "Beer", "Soft Drinks", "Wine", "Hard Liquor", "Coffee"];

const BeverageDisplay = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [settings, setSettings] = useState(null);
  const [notifiedOrders, setNotifiedOrders] = useState(new Set());
  const audioContextRef = useRef(null);
  const customAudioRef = useRef(null);

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    loadSettings();
    loadOrders();
    const orderInterval = setInterval(loadOrders, 3000);
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      clearInterval(orderInterval);
      clearInterval(clockInterval);
    };
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
      if (response.data?.notification_sound_url) {
        customAudioRef.current = new Audio(`${API_URL}${response.data.notification_sound_url}`);
        customAudioRef.current.load();
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const playNotificationSound = () => {
    if (!soundEnabled || settings?.notification_sound_enabled === false) return;
    
    if (settings?.notification_sound_url && customAudioRef.current) {
      customAudioRef.current.currentTime = 0;
      customAudioRef.current.play().catch(() => playDefaultSound());
    } else {
      playDefaultSound();
    }
  };

  const playDefaultSound = () => {
    if (!audioContextRef.current) return;
    [600, 750, 900].forEach((freq, i) => {
      setTimeout(() => {
        const osc = audioContextRef.current.createOscillator();
        const gain = audioContextRef.current.createGain();
        osc.connect(gain);
        gain.connect(audioContextRef.current.destination);
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.4, audioContextRef.current.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.25);
        osc.start();
        osc.stop(audioContextRef.current.currentTime + 0.25);
      }, i * 150);
    });
  };

  const loadOrders = async () => {
    try {
      const response = await orderAPI.getAll();
      // Only show orders that have been transferred (pending or preparing status)
      // Excludes pending_approval orders which are still waiting for staff approval
      const beverageOrders = response.data
        .filter(order => ['pending', 'preparing'].includes(order.status))
        .map(order => ({
          ...order,
          items: order.items.filter(item => BEVERAGE_CATEGORIES.includes(item.category))
        }))
        .filter(order => order.items.length > 0);

      // Check for new orders and play sound
      beverageOrders.forEach(order => {
        if (order.status === 'pending' && !notifiedOrders.has(order.id)) {
          playNotificationSound();
          setNotifiedOrders(prev => new Set([...prev, order.id]));
        }
      });

      setOrders(beverageOrders);
      setLoading(false);
    } catch (error) {
      console.error('Error loading orders:', error);
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await orderAPI.updateBeverageStatus(orderId, status);
      loadOrders();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getElapsedTime = (createdAt) => {
    const diff = Math.floor((new Date() - new Date(createdAt)) / 1000 / 60);
    return diff;
  };

  const getTimeColor = (minutes) => {
    if (minutes < 5) return 'text-emerald-500';
    if (minutes < 10) return 'text-amber-500';
    return 'text-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white font-medium">Loading Beverage Orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="beverage-display">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-40">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
              <Wine size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-black">BAR DISPLAY</h1>
              <p className="text-slate-400 text-sm">Drinks & Beverages Only • View Only</p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            {/* Stats */}
            <div className="flex gap-4">
              <div className="text-center">
                <p className="text-3xl font-black text-purple-500" data-testid="pending-count">{orders.filter(o => o.status === 'pending').length}</p>
                <p className="text-xs text-slate-400 uppercase">Pending</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-blue-500" data-testid="preparing-count">{orders.filter(o => o.status === 'preparing').length}</p>
                <p className="text-xs text-slate-400 uppercase">Making</p>
              </div>
            </div>

            {/* Clock */}
            <div className="text-center bg-slate-700 px-6 py-2 rounded-xl">
              <p className="text-3xl font-black font-mono">{currentTime.toLocaleTimeString()}</p>
              <p className="text-xs text-slate-400">{currentTime.toLocaleDateString()}</p>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`p-3 rounded-xl transition-all ${soundEnabled ? 'bg-emerald-600' : 'bg-slate-700'}`}
                title={soundEnabled ? 'Sound ON' : 'Sound OFF'}
                data-testid="sound-toggle"
              >
                {soundEnabled ? <Volume2 size={24} /> : <VolumeX size={24} />}
              </button>
              <button onClick={loadOrders} className="p-3 bg-slate-700 rounded-xl hover:bg-slate-600" title="Refresh" data-testid="refresh-btn">
                <RefreshCw size={24} />
              </button>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-2">
              <a href="/kitchen" className="flex items-center gap-2 px-4 py-3 bg-amber-600 rounded-xl font-bold hover:bg-amber-700" data-testid="nav-kitchen">
                <ChefHat size={20} />
                Kitchen
              </a>
              <a href="/pos" className="flex items-center gap-2 px-4 py-3 bg-indigo-600 rounded-xl font-bold hover:bg-indigo-700" data-testid="nav-pos">
                <Receipt size={20} />
                POS
              </a>
              <a href="/" className="flex items-center gap-2 px-4 py-3 bg-slate-700 rounded-xl font-bold hover:bg-slate-600" data-testid="nav-home">
                <Home size={20} />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Orders Grid */}
      <main className="p-6">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24" data-testid="empty-state">
            <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mb-6">
              <Coffee size={48} className="text-slate-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-400 mb-2">Bar is Clear!</h2>
            <p className="text-slate-500">No drink orders waiting</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {orders.map(order => {
              const elapsed = getElapsedTime(order.created_at);
              const isPending = order.status === 'pending';
              
              return (
                <div
                  key={order.id}
                  data-testid={`order-card-${order.id}`}
                  className={`rounded-2xl overflow-hidden ${
                    isPending 
                      ? 'bg-purple-500/20 border-2 border-purple-500 animate-pulse' 
                      : 'bg-slate-800 border border-slate-700'
                  }`}
                >
                  {/* Card Header */}
                  <div className={`px-4 py-3 flex items-center justify-between ${isPending ? 'bg-purple-500' : 'bg-blue-600'}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-3xl font-black">#{order.order_number}</span>
                      <span className="px-2 py-1 bg-black/20 rounded-lg text-sm font-bold">
                        Table {order.table}
                      </span>
                    </div>
                    <div className={`flex items-center gap-1 ${getTimeColor(elapsed)}`}>
                      <Clock size={18} />
                      <span className="font-bold">{elapsed}m</span>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="p-4 space-y-3">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-black text-purple-500">×{item.quantity}</span>
                            <span className="font-bold text-lg">{item.name?.en || item.name}</span>
                          </div>
                          {item.special_note && (
                            <p className="text-purple-400 text-sm mt-1 bg-purple-500/20 px-2 py-1 rounded">
                              {item.special_note}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="px-4 pb-4">
                    {isPending ? (
                      <button
                        onClick={() => updateStatus(order.id, 'preparing')}
                        className="w-full bg-purple-500 text-white py-4 rounded-xl font-black text-lg flex items-center justify-center gap-2 hover:bg-purple-400"
                        data-testid={`start-making-${order.id}`}
                      >
                        <PlayCircle size={24} />
                        START MAKING
                      </button>
                    ) : (
                      <button
                        onClick={() => updateStatus(order.id, 'ready')}
                        className="w-full bg-emerald-500 text-white py-4 rounded-xl font-black text-lg flex items-center justify-center gap-2 hover:bg-emerald-400"
                        data-testid={`mark-ready-${order.id}`}
                      >
                        <CheckCircle size={24} />
                        MARK READY
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default BeverageDisplay;
